import { useCallback, useState } from "react";
import { Box } from "@mui/material";
import { useAiChatContext } from "src/_modules/AiSupportChat/context/AiChatProvider";
import {
  PANEL_VIEWS,
  isConversationView,
} from "src/_modules/AiSupportChat/hooks/useAiChatPanel";
import {
  CHAT_PHASES,
  NOTICE_KINDS,
} from "src/_modules/AiSupportChat/core/chatReducer";
import PanelHeader from "src/_modules/AiSupportChat/components/PanelHeader";
import HomeView from "src/_modules/AiSupportChat/components/HomeView";
import ConversationView from "src/_modules/AiSupportChat/components/ConversationView";
import InlineNotice from "src/_modules/AiSupportChat/components/InlineNotice";

/*
 * Each view puts `autoFocus` on its primary control: views remount on open
 * and on every view change, so focus lands there (plan §3.3) instead of on
 * <body> when the button that changed the view disappears.
 */

/**
 * Panel content: header + the current view (home | draft | conversation).
 * Layout-agnostic; `PanelShell` decides where it lives.
 *
 * The panel view and the chat state are separate (T07): every way into a
 * conversation goes through here, so both always change together.
 *
 * @param {{ titleId: string }} props
 */
export default function AiChatPanel({ titleId }) {
  const { panel, chat, isAiDeveloper, debugOn, toggleDebug } =
    useAiChatContext();
  const { view, close, showHome, showDraft, showConversation } = panel;
  const {
    state,
    startNew: startDraft,
    openConversation,
    send,
    dismissNotice,
  } = chat;
  const inConversation = view !== PANEL_VIEWS.HOME;
  // While an answer is in flight, nothing may replace the conversation (C17)
  const busy = state.phase !== CHAT_PHASES.IDLE;
  const liveConversationId = busy ? (state.conversation?.id ?? null) : null;
  // Each new draft remounts the view, also from a draft ("+"): an empty
  // composer and a fresh list
  const [draftKey, setDraftKey] = useState(0);

  /**
   * Empty draft (or the text a 855/856 couldn't send), or `prefill` (Retry
   * in a closed conversation).
   *
   * @returns {boolean} whether a draft was started (refused while busy)
   */
  const startNew = useCallback(
    (prefill) => {
      if (!startDraft(prefill)) return false;
      setDraftKey((key) => key + 1);
      showDraft();
      return true;
    },
    [startDraft, showDraft]
  );

  // A suggestion starts a new conversation and sends at once (plan §3.5).
  // The draft is prefilled with it: if it can't go out yet (853 cooldown,
  // 852), the question waits in the composer; once sent, nothing is left
  const sendSuggestion = useCallback(
    (text) => {
      if (startNew(text)) send(text);
    },
    [startNew, send]
  );

  // The live conversation only switches the view (C17, inside openConversation)
  const openSaved = useCallback(
    (id) => {
      openConversation(id);
      showConversation(id);
    },
    [openConversation, showConversation]
  );

  return (
    <>
      <PanelHeader
        titleId={titleId}
        onClose={close}
        available={state.available}
        onBack={inConversation ? showHome : undefined}
        onNewConversation={inConversation ? () => startNew() : undefined}
        newConversationDisabled={busy}
        // AI developers only: the merchant header has no debug button
        onDebugToggle={isAiDeveloper ? toggleDebug : undefined}
        debugOn={debugOn}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {inConversation ? (
          <ConversationView
            key={
              isConversationView(view)
                ? view.conversationId
                : `draft-${draftKey}`
            }
            conversationId={
              isConversationView(view) ? view.conversationId : null
            }
            onStartNew={startNew}
          />
        ) : (
          <>
            {/* 855: the conversation expired or was deleted meanwhile */}
            <InlineNotice
              notice={
                state.notice?.kind === NOTICE_KINDS.NOT_FOUND
                  ? state.notice
                  : null
              }
              onDismiss={dismissNotice}
            />
            <HomeView
              onStartNew={() => startNew()}
              onSuggestion={sendSuggestion}
              onOpenConversation={openSaved}
              busy={busy}
              liveConversationId={liveConversationId}
            />
          </>
        )}
      </Box>
    </>
  );
}
